const STORAGE = {
  ASKED: 'locationPermissionAsked',
  STATUS: 'locationPermissionStatus',
  LAST: 'lastKnownLocation',
};

export const LOCATION_PERMISSION = {
  GRANTED: 'granted',
  DENIED: 'denied',
  UNSUPPORTED: 'unsupported',
};

export const LIVE_LOCATION_EVENT = 'liveLocationUpdated';

const FAST_GPS = {
  enableHighAccuracy: false,
  timeout: 8000,
  maximumAge: 30000,
};

const ACCURATE_GPS = {
  enableHighAccuracy: true,
  timeout: 20000,
  maximumAge: 0,
};

let inFlightRequest = null;

export function getLocationPermissionStatus() {
  return localStorage.getItem(STORAGE.STATUS);
}

export function hasLocationPermissionBeenAsked() {
  return localStorage.getItem(STORAGE.ASKED) === 'true';
}

export function isLocationGranted() {
  return getLocationPermissionStatus() === LOCATION_PERMISSION.GRANTED;
}

export function isLocationDenied() {
  return getLocationPermissionStatus() === LOCATION_PERMISSION.DENIED;
}

export function isLocationSupported() {
  return Boolean(navigator.geolocation);
}

export function getStoredLocation() {
  try {
    const raw = localStorage.getItem(STORAGE.LAST);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getFreshStoredLocation(maxAgeMs = 30 * 60 * 1000) {
  const stored = getStoredLocation();
  if (!stored || stored.lat == null || stored.lng == null) return null;
  if (Date.now() - (stored.updatedAt || 0) > maxAgeMs) return null;
  return stored;
}

function saveLastLocation(lat, lng, accuracy) {
  localStorage.setItem(
    STORAGE.LAST,
    JSON.stringify({
      lat,
      lng,
      accuracy: Number.isFinite(accuracy) ? accuracy : null,
      updatedAt: Date.now(),
    })
  );
}

function markPermission(status) {
  localStorage.setItem(STORAGE.ASKED, 'true');
  localStorage.setItem(STORAGE.STATUS, status);
}

function readPosition(pos) {
  const lat = pos.coords.latitude;
  const lng = pos.coords.longitude;
  saveLastLocation(lat, lng, pos.coords.accuracy);
  const payload = {
    lat,
    lng,
    accuracy: pos.coords.accuracy,
    coords: `${lat},${lng}`,
  };
  window.dispatchEvent(new CustomEvent(LIVE_LOCATION_EVENT, { detail: payload }));
  return payload;
}

function getPosition(options) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

/**
 * Ask for location and capture coordinates. Safe to call repeatedly.
 */
export function requestLocationPermissionOnce() {
  if (inFlightRequest) return inFlightRequest;

  inFlightRequest = (async () => {
    if (!navigator.geolocation) {
      markPermission(LOCATION_PERMISSION.UNSUPPORTED);
      return LOCATION_PERMISSION.UNSUPPORTED;
    }

    try {
      const pos = await getPosition(FAST_GPS);
      markPermission(LOCATION_PERMISSION.GRANTED);
      readPosition(pos);
      getPosition(ACCURATE_GPS).then(readPosition).catch(() => {});
      return LOCATION_PERMISSION.GRANTED;
    } catch {
      try {
        const pos = await getPosition(ACCURATE_GPS);
        markPermission(LOCATION_PERMISSION.GRANTED);
        readPosition(pos);
        return LOCATION_PERMISSION.GRANTED;
      } catch {
        if (getStoredLocation()) {
          markPermission(LOCATION_PERMISSION.GRANTED);
          return LOCATION_PERMISSION.GRANTED;
        }
        markPermission(LOCATION_PERMISSION.DENIED);
        return LOCATION_PERMISSION.DENIED;
      }
    }
  })().finally(() => {
    inFlightRequest = null;
  });

  return inFlightRequest;
}

/**
 * Fetch the most exact GPS available: high-accuracy first, then network, then last stored.
 * Returns "lat,lng".
 */
export async function fetchCurrentLocation({ silent = false } = {}) {
  try {
    const pos = await getPosition(ACCURATE_GPS);
    markPermission(LOCATION_PERMISSION.GRANTED);
    return readPosition(pos).coords;
  } catch {
    try {
      const pos = await getPosition(FAST_GPS);
      markPermission(LOCATION_PERMISSION.GRANTED);
      const coords = readPosition(pos).coords;
      getPosition(ACCURATE_GPS).then(readPosition).catch(() => {});
      return coords;
    } catch (err) {
      const stored = getStoredLocation();
      if (stored?.lat != null && stored?.lng != null) {
        return `${stored.lat},${stored.lng}`;
      }
      if (!silent) throw err;
      throw err;
    }
  }
}

export function watchLiveLocation(onUpdate, onError) {
  if (!navigator.geolocation) {
    onError?.(new Error('Geolocation is not supported'));
    return () => {};
  }

  const watchId = navigator.geolocation.watchPosition(
    (pos) => {
      markPermission(LOCATION_PERMISSION.GRANTED);
      onUpdate?.(readPosition(pos));
    },
    (err) => {
      onError?.(err);
    },
    ACCURATE_GPS
  );

  return () => {
    navigator.geolocation.clearWatch(watchId);
  };
}

export function formatLocationString(lat, lng) {
  return `${lat},${lng}`;
}
